import { Link } from "react-router-dom";
import { AppLayout } from "@/design-system/layout/AppLayout";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  LineChart,
  Line,
} from "recharts";
import { AlertCircle, CheckCircle2, Clock, MapPin, Users, Trophy, Calendar, MoreHorizontal } from "lucide-react";

/* ─── Mock Data ─────────────────────────────────────────────── */
const tournamentData = [
  { month: "Jan", tournaments: 3, teams: 24, players: 288 },
  { month: "Feb", tournaments: 5, teams: 42, players: 504 },
  { month: "Mar", tournaments: 4, teams: 36, players: 432 },
  { month: "Apr", tournaments: 7, teams: 64, players: 768 },
  { month: "May", tournaments: 6, teams: 56, players: 672 },
];

const scheduleData = [
  { week: "W1", requests: 12 },
  { week: "W2", requests: 18 },
  { week: "W3", requests: 15 },
  { week: "W4", requests: 22 },
  { week: "W5", requests: 19 },
];

const needsAttention = [
  {
    severity: "error",
    title: "Roster ingestion failed",
    detail: "KC Heat 16U — 8 players not imported",
  },
  {
    severity: "warning",
    title: "Pending schedule request",
    detail: "Jefferson Prep Basketball — waiting your review",
  },
  {
    severity: "info",
    title: "Tournament draft incomplete",
    detail: "Summer Showcase 2024 — missing divisions and age groups",
  },
];

const upcomingEvents = [
  {
    id: 1,
    name: "KC Youth Basketball Classic",
    status: "published",
    dates: "May 15-16, 2024",
    venue: "Kansas City, MO",
    teams: 12,
    players: 144,
  },
  {
    id: 2,
    name: "Girls Volleyball Showcase",
    status: "registrationOpen",
    dates: "May 20-21, 2024",
    venue: "Overland Park, KS",
    teams: 8,
    players: 96,
  },
  {
    id: 3,
    name: "Summer Showdown 2024",
    status: "draft",
    dates: "June 10-12, 2024",
    venue: "Topeka, KS",
    teams: 0,
    players: 0,
  },
];

const recentActivity = [
  {
    type: "team_registration",
    team: "Kansas City Heat 16U",
    tournament: "KC Youth Basketball Classic",
    timestamp: "3h ago",
  },
  {
    type: "schedule_request",
    team: "Jefferson Prep Basketball",
    detail: "Requested travel time accommodation",
    timestamp: "1d ago",
  },
  {
    type: "tournament_published",
    tournament: "Girls Volleyball Showcase",
    detail: "Now accepting registrations",
    timestamp: "2d ago",
  },
  {
    type: "roster_added",
    team: "St. Louis United 14U",
    detail: "Added 14 players to roster",
    timestamp: "3d ago",
  },
];

/* ─── Status Badge ───────────────────────────────────────────── */
function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    draft: "bg-secondary text-foreground/60",
    published: "bg-success-light text-success",
    registrationOpen: "bg-info-light text-info",
    inProgress: "bg-warning-light text-warning",
    completed: "bg-muted text-muted-foreground",
  };
  const labels: Record<string, string> = {
    draft: "Draft",
    published: "Published",
    registrationOpen: "Registration Open",
    inProgress: "In Progress",
    completed: "Completed",
  };
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${styles[status] || "bg-muted"}`}>
      {labels[status] || status}
    </span>
  );
}

/* ─── Severity Icon ──────────────────────────────────────────── */
function SeverityIcon({ severity }: { severity: string }) {
  const icons: Record<string, React.ReactNode> = {
    error: <AlertCircle className="w-5 h-5 text-error" />,
    warning: <Clock className="w-5 h-5 text-warning" />,
    info: <AlertCircle className="w-5 h-5 text-info" />,
  };
  return icons[severity];
}

/* ─── Activity Icon ──────────────────────────────────────────── */
function ActivityIcon({ type }: { type: string }) {
  const icons: Record<string, React.ReactNode> = {
    team_registration: <Users className="w-4 h-4" />,
    schedule_request: <Calendar className="w-4 h-4" />,
    tournament_published: <Trophy className="w-4 h-4" />,
    roster_added: <Users className="w-4 h-4" />,
  };
  return (
    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
      {icons[type]}
    </div>
  );
}

/* ─── Stat Card ─────────────────────────────────────────────── */
function StatCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string | number }) {
  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
          {icon}
        </div>
        <MoreHorizontal size={16} className="text-muted-foreground" />
      </div>
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      <p className="text-3xl font-bold text-foreground">{value}</p>
    </div>
  );
}

function ChartTooltip({ active, payload, label }: any) {
  if (active && payload && payload.length) {
    return (
      <div className="bg-navy text-white rounded-xl shadow-xl px-3 py-2 text-xs">
        <p className="font-bold">{label}</p>
        {payload.map((p: any) => (
          <p key={p.name}>
            {p.name}: {p.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
}

/* ─── RLevents Dashboard ────────────────────────────────────── */
export default function RLEventsDashboard() {
  return (
    <AppLayout userName="Sarah Johnson" userRole="Tournament Operator">
      <div className="min-h-screen bg-background px-4 sm:px-6 lg:px-8 py-6 space-y-6">

        {/* Greeting */}
        <div>
          <h2 className="text-2xl font-bold text-foreground">Dashboard</h2>
          <p className="text-sm text-muted-foreground">Welcome back, Sarah! Here's your tournament overview.</p>
        </div>

        {/* ── Top Stats ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            icon={<Trophy className="w-6 h-6" />}
            label="Active Tournaments"
            value="3"
          />
          <StatCard
            icon={<Users className="w-6 h-6" />}
            label="Teams Registered"
            value="20"
          />
          <StatCard
            icon={<Users className="w-6 h-6" />}
            label="Players Rostered"
            value="240"
          />
          <StatCard
            icon={<Calendar className="w-6 h-6" />}
            label="Schedule Requests"
            value="7"
          />
        </div>

        {/* ── Needs Your Attention ── */}
        <div className="bg-white rounded-2xl p-5 shadow-sm">
          <h3 className="font-bold text-foreground mb-4">Needs your attention</h3>
          {needsAttention.length > 0 ? (
            <div className="space-y-3">
              {needsAttention.map((item, i) => {
                const bgColors: Record<string, string> = {
                  error: "bg-error-light border-error/20",
                  warning: "bg-warning-light border-warning/20",
                  info: "bg-info-light border-info/20",
                };
                return (
                  <div
                    key={i}
                    className={`flex items-start gap-4 rounded-lg border p-4 cursor-pointer hover:shadow-md transition-all ${
                      bgColors[item.severity]
                    }`}
                  >
                    <div className="flex-shrink-0 mt-0.5">{<SeverityIcon severity={item.severity} />}</div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-foreground text-sm">{item.title}</h4>
                      <p className="text-muted-foreground text-sm mt-1">{item.detail}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-6">
              <CheckCircle2 className="w-10 h-10 text-success mx-auto mb-2" />
              <p className="text-foreground font-semibold">Nothing urgent right now</p>
            </div>
          )}
        </div>

        {/* ── Charts ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Tournaments by month */}
          <div className="bg-white rounded-2xl p-5 shadow-sm">
            <h3 className="font-bold text-foreground mb-4">Tournaments Created</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={tournamentData}>
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "hsl(230 15% 55%)" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "hsl(230 15% 55%)" }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="tournaments" fill="hsl(300 77% 55%)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Schedule requests over time */}
          <div className="bg-white rounded-2xl p-5 shadow-sm">
            <h3 className="font-bold text-foreground mb-4">Schedule Requests</h3>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={scheduleData}>
                <XAxis dataKey="week" tick={{ fontSize: 11, fill: "hsl(230 15% 55%)" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "hsl(230 15% 55%)" }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltip />} />
                <Line
                  type="monotone"
                  dataKey="requests"
                  stroke="hsl(300 77% 55%)"
                  strokeWidth={2.5}
                  dot={false}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* ── Upcoming Tournaments ── */}
        <div className="bg-white rounded-2xl p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-foreground">Upcoming Tournaments</h3>
            <Link to="/events" className="text-sm text-primary hover:underline font-medium">
              View all →
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {upcomingEvents.map((tourn) => (
              <div key={tourn.id} className="border border-border rounded-2xl p-4 hover:shadow-md transition-all cursor-pointer">
                <div className="flex items-start justify-between mb-3">
                  <Link
                    to={`/events/${tourn.id}`}
                    className="font-semibold text-foreground hover:text-primary transition-colors"
                  >
                    {tourn.name}
                  </Link>
                  <StatusBadge status={tourn.status} />
                </div>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {tourn.dates}
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    {tourn.venue}
                  </div>
                  <div className="pt-2 border-t border-border">
                    <p className="text-foreground">{tourn.teams} teams • {tourn.players} players</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Recent Activity ── */}
        <div className="bg-white rounded-2xl p-5 shadow-sm">
          <h3 className="font-bold text-foreground mb-4">Recent Activity</h3>
          <div className="space-y-4">
            {recentActivity.map((item, i) => (
              <div
                key={i}
                className={`flex gap-3 ${
                  i < recentActivity.length - 1 ? "pb-4 border-b border-border" : ""
                }`}
              >
                <ActivityIcon type={item.type} />
                <div className="flex-1 min-w-0">
                  {item.type === "team_registration" && (
                    <p className="text-sm text-foreground">
                      <span className="font-medium">{item.team}</span> registered for{" "}
                      <span className="font-medium">{item.tournament}</span>
                    </p>
                  )}
                  {item.type === "schedule_request" && (
                    <p className="text-sm text-foreground">
                      <span className="font-medium">{item.team}</span> — {item.detail}
                    </p>
                  )}
                  {item.type === "tournament_published" && (
                    <p className="text-sm text-foreground">
                      <span className="font-medium">{item.tournament}</span> — {item.detail}
                    </p>
                  )}
                  {item.type === "roster_added" && (
                    <p className="text-sm text-foreground">
                      <span className="font-medium">{item.team}</span> — {item.detail}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">{item.timestamp}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
